import React, { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import Header from '../components/layout/Header';
import Footer from '../components/layout/Footer';
import { useSiteData } from '../context/SiteDataContext';
import { ChevronDown, ChevronUp, Phone, Check } from 'lucide-react';

const ReprogrammationPage = () => {
  const { reprogrammingServices, companyInfo, pageContent } = useSiteData();
  const hero = pageContent?.reprogHero || {};
  const [expandedService, setExpandedService] = useState(null);

  return (
    <div className="min-h-screen bg-black font-sans">
      <Helmet>
        <title>Reprogrammation Moteur Stage 1 & 2 | Ecotint & Reprog - Eugénie les Bains (40)</title>
        <meta name="description" content="Reprogrammation moteur Stage 1 à partir de 299€, Stage 2, conversion E85 à 399€. Solutions AdBlue, EGR, FAP. Expert à Eugénie les Bains dans les Landes." />
        <link rel="canonical" href="https://ecotint-reprog.fr/reprogrammation" />
      </Helmet>
      <Header />
      
      {/* Hero - Full screen */}
      <section className="h-screen w-full relative flex items-center" data-testid="reprog-hero">
        <div 
          className="absolute inset-0 bg-cover bg-center"
          style={{ backgroundImage: `url(${hero.bgImage || 'https://images.unsplash.com/photo-1619405399517-d7fce0f13302?w=1920'})` }}
        />
        <div className="absolute inset-0 bg-gradient-to-r from-black via-black/80 to-black/40" />
        
        <div className="relative z-10 w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-12">
          <div className="max-w-2xl">
            <span className="text-red-500 text-xs sm:text-sm font-medium uppercase tracking-[0.2em]">
              {hero.category || 'Performance'}
            </span>
            <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-7xl font-bold text-white mt-3 sm:mt-4 mb-4 sm:mb-6 leading-tight">
              {hero.title || 'Reprogrammation'} <span className="text-red-500">{hero.titleAccent || 'Moteur'}</span>
            </h1>
            <p className="text-sm sm:text-base md:text-xl text-white/70 max-w-xl">
              {hero.desc || 'Stage 1, Stage 2, conversion E85 et solutions de dépollution. Libérez le potentiel de votre véhicule.'}
            </p>
          </div>
        </div>
      </section>

      {/* Simulator DriveLab */}
      <section className="py-12 sm:py-20 bg-neutral-950">
        <div className="w-full px-4 sm:px-6 lg:px-12">
          <div className="text-center mb-8 sm:mb-12">
            <span className="text-red-500 text-xs sm:text-sm font-medium uppercase tracking-[0.2em]">
              Simulateur
            </span>
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-white mt-4">
              Estimez vos <span className="text-red-500">gains</span>
            </h2>
          </div>
          <div className="w-full bg-white/5 rounded-2xl sm:rounded-3xl p-2 sm:p-4">
            <iframe 
              src="https://drivelabz.fr/embed?key=drivelab_TI2etG-h498cIRkaT-QsnUXeqBmMG7tDIEd32kNyPl8"
              width="100%"
              height="700"
              frameBorder="0"
              style={{ border: 'none' }}
              title="Simulateur de Reprogrammation"
              className="w-full rounded-xl sm:rounded-2xl"
              allowFullScreen
            />
          </div>
        </div>
      </section>

      {/* Pricing */}
      <section className="py-12 sm:py-20 bg-black">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-12">
          <div className="text-center mb-16">
            <span className="text-red-500 text-sm font-medium uppercase tracking-[0.2em]">
              Tarifs {new Date().getFullYear()}
            </span>
            <h2 className="text-3xl md:text-4xl font-bold text-white mt-4">
              Nos <span className="text-red-500">Prestations</span>
            </h2>
            <p className="text-white/50 mt-4">Cliquez sur une prestation pour plus de détails</p>
          </div>

          <div className="space-y-3">
            {reprogrammingServices.map((service) => (
              <div
                key={service.id}
                className="bg-white/5 rounded-2xl overflow-hidden border border-white/10 hover:border-red-500/30 transition-colors"
              >
                <button
                  onClick={() => setExpandedService(expandedService === service.id ? null : service.id)}
                  className="w-full px-6 py-5 flex items-center justify-between text-left"
                >
                  <span className="text-white font-medium text-lg">{service.shortName}</span>
                  <div className="flex items-center gap-6">
                    <span className="text-2xl font-bold text-red-500">{service.price} €</span>
                    {expandedService === service.id ? (
                      <ChevronUp size={24} className="text-white/40" />
                    ) : (
                      <ChevronDown size={24} className="text-white/40" />
                    )}
                  </div>
                </button>

                {expandedService === service.id && (
                  <div className="px-6 pb-6 border-t border-white/10">
                    <div className="pt-6">
                      <p className="text-white/60 mb-6">{service.description}</p>
                      {service.features && service.features.length > 0 && (
                        <div className="space-y-3 mb-6">
                          {service.features.map((feature, index) => (
                            <div key={index} className="flex items-center gap-3">
                              <div className="w-6 h-6 rounded-full bg-red-600/20 flex items-center justify-center flex-shrink-0">
                                <Check size={14} className="text-red-500" />
                              </div>
                              <span className="text-white/80">{feature}</span>
                            </div>
                          ))}
                        </div>
                      )}
                      <a
                        href={`tel:${companyInfo.phone.replace(/\s/g, '')}`}
                        className="inline-flex items-center gap-2 bg-red-600 hover:bg-red-700 text-white px-6 py-3 rounded-full font-medium transition-colors"
                      >
                        <Phone size={18} />
                        Prendre rendez-vous
                      </a>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>

          <p className="text-center text-white/40 mt-12">
            D'autres solutions ou combinaisons sont possibles. Contactez-nous pour un devis personnalisé.
          </p>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default ReprogrammationPage;
